import { formatDateTime, formatMoney } from '../shared/format';
import { workerEventLine } from '../shared/labels';

import { LifecycleEvent } from '@/services/lifecycleAPI';

const FIELD_LABELS: Record<string, string> = {
  holdout_percent: 'Holdout',
  high_demand_threshold: 'High-demand threshold',
  enabled: 'Journey',
  sms_enabled: 'SMS',
  mode: 'Mode',
  cohort_start_on: 'Cohort start date',
  cohort_zip_prefixes: 'Zip prefixes',
  monthly_sms_budget_cents: 'SMS budget',
  sms_segment_cost_cents: 'SMS segment cost',
  tax_guide_url: 'Tax guide link',
  recipient_allowlist: 'Allowlist',
  sms_paused_at: 'SMS paused at',
  sms_pause_reason: 'SMS pause reason',
};

const fieldLabel = (field: string) =>
  FIELD_LABELS[field] ?? field.replace(/_/g, ' ');

const formatValue = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '(none)';
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (field === 'holdout_percent') return `${value}%`;
  if (field === 'monthly_sms_budget_cents' && typeof value === 'number') {
    return formatMoney(value);
  }
  // decimal(6,3) cents, so fractions like 0.83 are real; the API may send it
  // as text.
  if (field === 'sms_segment_cost_cents' && Number.isFinite(Number(value))) {
    return `${Number(Number(value).toFixed(3))}¢`;
  }
  if (field === 'cohort_start_on' && typeof value === 'string') {
    const date = new Date(`${value}T12:00:00Z`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      });
    }
  }
  if (field === 'sms_paused_at' && typeof value === 'string') {
    return formatDateTime(value);
  }
  if (Array.isArray(value)) {
    return value.every((v) => typeof v !== 'object')
      ? value.join(', ') || '(none)'
      : 'changed';
  }
  if (typeof value === 'object') return 'changed';
  return String(value);
};

const isPresent = (value: unknown) => value !== null && value !== undefined;

const copyDetails = (event: LifecycleEvent): string[] => {
  const payload = event.payload ?? {};
  const { version, reverted_from_version: from } = payload;
  const suffix = payload.locale === 'es' ? ' (Spanish)' : '';
  let text: string | null = null;
  if (event.event_key === 'copy_reverted') {
    if (isPresent(from) && isPresent(version)) {
      text = `Back to version ${from}, published as version ${version}`;
    } else if (isPresent(from)) {
      text = `Back to version ${from}`;
    } else if (isPresent(version)) {
      text = `Published as version ${version}`;
    }
  } else if (event.event_key === 'copy_reset') {
    text = isPresent(version)
      ? `Default text from the code, published as version ${version}`
      : 'Default text from the code';
  } else if (isPresent(version)) {
    text = `Version ${version}`;
  }
  return text ? [`${text}${suffix}`] : [];
};

export const eventDetails = (event: LifecycleEvent): string[] => {
  const payload = event.payload ?? {};
  switch (event.event_key) {
    case 'journey_settings_changed': {
      const changes =
        payload.changes && typeof payload.changes === 'object'
          ? (payload.changes as Record<string, unknown>)
          : {};
      const lines = Object.entries(changes).map(([field, pair]) => {
        const [before, after] = Array.isArray(pair) ? pair : [null, pair];
        return `${fieldLabel(field)} ${formatValue(field, before)} → ${formatValue(field, after)}`;
      });
      return lines.length ? lines : ['Settings changed'];
    }
    case 'sms_paused': {
      const reason = payload.reason;
      return [
        ...(typeof reason === 'string' && reason ? [reason] : []),
        'Push and email continue.',
      ];
    }
    case 'sms_resumed':
      return ['SMS pause cleared'];
    case 'copy_published':
    case 'copy_reverted':
    case 'copy_reset':
      return copyDetails(event);
    default: {
      const line = workerEventLine(event.event_key, payload);
      return line ? [line] : [];
    }
  }
};

export const eventTarget = (event: LifecycleEvent): string => {
  const payload = event.payload ?? {};
  if (typeof payload.step_key === 'string' && payload.step_key) {
    return payload.step_key;
  }
  if (payload.journey_key === '_global') return 'Engine (global)';
  if (typeof payload.journey_key === 'string' && payload.journey_key) {
    return payload.journey_key;
  }
  return '-';
};
